import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountService } from 'src/app/services/account.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import {
    TWO_FACTOR_CODE_LENGTH,
    TWO_FACTOR_RESEND_COOLDOWN_SECONDS,
} from 'src/app/shared/constants/accountSettings.constants';
import { AccountOtpInputComponent } from '../account-otp-input/account-otp-input.component';

@Component({
    standalone: false,
    selector: 'app-account-two-factor-otp',
    templateUrl: './account-two-factor-otp.component.html',
    styleUrls: ['./account-two-factor-otp.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class AccountTwoFactorOtpComponent implements OnInit, OnDestroy {
    @Input() disabling = false;
    @Output() cancelled = new EventEmitter<void>();
    @Output() verified = new EventEmitter<void>();

    @ViewChild(AccountOtpInputComponent)
    private otpInput?: AccountOtpInputComponent;

    readonly codeLength = TWO_FACTOR_CODE_LENGTH;

    code = '';
    canVerify = false;
    verifying = false;
    sending = false;
    invalid = false;
    resendCooldown = TWO_FACTOR_RESEND_COOLDOWN_SECONDS;

    private resendTimer?: ReturnType<typeof setInterval>;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private accountService: AccountService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.startResendCooldown();
    }

    onCodeChange(code: string): void {
        this.code = code;
        if (code) this.invalid = false;
        this.refreshCanVerify();
    }

    verify(): void {
        if (!this.canVerify) return;

        this.verifying = true;
        this.refreshCanVerify();
        this.accountService
            .verifyOtp(this.code)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.verifying = false;
                    this.clearResendTimer();
                    this.verified.emit();
                },
                error: () => {
                    this.verifying = false;
                    this.invalid = true;
                    this.code = '';
                    this.refreshCanVerify();
                    this.otpInput?.clear();
                },
            });
    }

    resend(): void {
        if (this.resendCooldown > 0 || this.sending) return;

        this.sending = true;
        this.accountService
            .enableMfa(!this.disabling)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.invalid = false;
                    this.code = '';
                    this.refreshCanVerify();
                    this.otpInput?.clear();
                    this.startResendCooldown();
                    this.toastService.showSuccess('A new code has been sent', true);
                },
                error: (err: HttpErrorResponse) => {
                    this.sending = false;
                    this.toastService.showError(
                        err,
                        this.translateService.getDynamicTranslation(
                            this.disabling ? 'Error disabling MFA.' : 'Error enabling MFA.',
                        ),
                    );
                },
            });
    }

    ngOnDestroy(): void {
        this.clearResendTimer();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private refreshCanVerify(): void {
        this.canVerify = this.code.length === TWO_FACTOR_CODE_LENGTH && !this.verifying;
    }

    private startResendCooldown(): void {
        this.resendCooldown = TWO_FACTOR_RESEND_COOLDOWN_SECONDS;
        this.clearResendTimer();
        this.resendTimer = setInterval(() => {
            this.resendCooldown = Math.max(0, this.resendCooldown - 1);
            if (this.resendCooldown === 0) this.clearResendTimer();
        }, 1000);
    }

    private clearResendTimer(): void {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = undefined;
        }
    }
}
